import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HERO_IMG =
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80';

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section id="hero" className="relative overflow-hidden bg-hero-warm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <div className="space-y-8">
            <div
              className="inline-flex items-center gap-2 bg-teal/8 border border-teal/20 px-4 py-2"
              style={{ borderRadius: '999px' }}
            >
              <Sparkles className="w-4 h-4 text-teal" />
              <span className="text-teal text-sm font-semibold">{t.hero.badge}</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold text-ink font-heading leading-[1.08] tracking-tight text-balance">
                {t.hero.titleA}{' '}
                <span className="font-accent italic text-teal font-normal">{t.hero.titleB}</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted leading-relaxed max-w-xl">{t.hero.lead}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/s"
                className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-dark text-white px-8 py-4 font-bold text-base transition-all shadow-card hover:-translate-y-0.5"
                style={{ borderRadius: '14px' }}
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#platform"
                className="inline-flex items-center justify-center gap-2 border-2 border-line hover:border-teal/40 text-ink hover:text-teal px-8 py-4 font-semibold text-base transition-all bg-surface"
                style={{ borderRadius: '14px' }}
              >
                {t.hero.ctaSecondary}
              </a>
            </div>

            <p className="text-xs text-muted-light leading-relaxed max-w-xl">{t.hero.legal}</p>
          </div>

          <div className="relative">
            <div
              className="relative aspect-[5/6] max-h-[540px] overflow-hidden shadow-float ring-1 ring-line"
              style={{ borderRadius: '2rem' }}
            >
              <img
                src={HERO_IMG}
                alt={t.reasons.imageAlt}
                className="w-full h-full object-cover"
                width={600}
                height={720}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />

              <div
                className="absolute top-5 right-5 bg-coral text-white text-xs font-bold px-3 py-2 shadow-soft"
                style={{ borderRadius: '10px' }}
              >
                {t.hero.chip}
              </div>

              <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-2">
                {t.hero.bento.map((item) => (
                  <div
                    key={item.label}
                    className="bg-surface/95 backdrop-blur border border-line p-3 text-center"
                    style={{ borderRadius: '12px' }}
                  >
                    <p className="text-teal font-extrabold text-sm sm:text-base leading-none">{item.value}</p>
                    <p className="text-muted text-[10px] sm:text-xs mt-1 leading-tight">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="absolute -z-10 -bottom-6 -left-6 w-32 h-32 bg-coral/15 blur-2xl"
              style={{ borderRadius: '50%' }}
              aria-hidden
            />
            <div
              className="absolute -z-10 -top-4 -right-4 w-24 h-24 bg-teal/15 blur-2xl"
              style={{ borderRadius: '50%' }}
              aria-hidden
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
